/**
 * Til tanlash — landing Header bilan bir xil dropdown (kod + strelka, ro‘yxatda kod + nom + belgi).
 * `fixedMenu`: admin topbar kabi overflow/scroll ichida — ro‘yxat document.body ga portal.
 */

import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../contexts/LanguageContext';
import './LanguageDropdown.css';

export interface LanguageDropdownProps {
  className?: string;
  menuClassName?: string;
  /** Mobil "Boshqa" panelida tanlangandan keyin yopish */
  onAfterSelect?: () => void;
  ariaLabel?: string;
  /** "Boshqa" menyusi: to‘liq kenglik */
  variant?: 'default' | 'block';
  /**
   * true: menyuni `position: fixed` + portal (topbar mobil overflow uchun).
   * false: oddiy absolute (landing header).
   */
  fixedMenu?: boolean;
}

export function LanguageDropdown({
  className,
  menuClassName,
  onAfterSelect,
  ariaLabel,
  variant = 'default',
  fixedMenu = false,
}: LanguageDropdownProps) {
  const { language, switchLanguage, availableLanguages, getCurrentLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [fixedStyle, setFixedStyle] = useState<React.CSSProperties>({});
  const currentLang = getCurrentLanguage();

  useLayoutEffect(() => {
    if (!open || !fixedMenu) return;
    const update = () => {
      const btn = triggerRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const minW = 168;
      if (variant === 'block') {
        setFixedStyle({
          position: 'fixed',
          top: r.bottom + 8,
          left: r.left,
          width: r.width,
          minWidth: minW,
          zIndex: 10050,
        });
      } else {
        const w = Math.max(minW, r.width);
        setFixedStyle({
          position: 'fixed',
          top: r.bottom + 8,
          left: r.right - w,
          width: w,
          zIndex: 10050,
        });
      }
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, fixedMenu, variant]);

  useEffect(() => {
    if (!open) return undefined;

    const onDoc = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };

    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onDoc, true);
      document.addEventListener('touchstart', onDoc, { capture: true, passive: true });
      document.addEventListener('click', onDoc, true);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('touchstart', onDoc, true);
      document.removeEventListener('click', onDoc, true);
    };
  }, [open]);

  const handlePick = (code: string) => {
    switchLanguage(code);
    setOpen(false);
    onAfterSelect?.();
  };

  const rootClass = [
    'language-dropdown',
    variant === 'block' ? 'language-dropdown--block' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const menuBody = (
    <>
      {availableLanguages.map((lang: { code: string; nativeName: string }) => (
        <button
          key={lang.code}
          type="button"
          className={`language-option ${language === lang.code ? 'active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            handlePick(lang.code);
          }}
        >
          <span className="language-option-code">{lang.code.toUpperCase()}</span>
          <span className="language-option-name">{lang.nativeName}</span>
          {language === lang.code && (
            <svg className="language-check" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
      ))}
    </>
  );

  const menuClass = `language-dropdown-menu ${menuClassName ?? ''}`.trim();

  return (
    <div className={rootClass} ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className="language-dropdown-toggle"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        aria-label={ariaLabel ?? 'Tilni tanlash'}
        aria-expanded={open}
      >
        <span className="language-code">{currentLang.code.toUpperCase()}</span>
        <svg className={`language-arrow ${open ? 'open' : ''}`} width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden>
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && !fixedMenu && <div className={menuClass}>{menuBody}</div>}
      {open &&
        fixedMenu &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className={`${menuClass} language-dropdown-menu--fixed`.trim()} ref={menuRef} style={fixedStyle}>
            {menuBody}
          </div>,
          document.body,
        )}
    </div>
  );
}
